import React, {createContext, PropsWithChildren, useContext, useEffect, useState} from 'react';
import {Exercise} from "myfitworld-model";
import {firestore} from "../firebase";
import useOrganization from "../hooks/useOrganization";
import {filterByOrganization} from "../api/dataInterfaceFactory";
import clientSideContentFilter from "../api/clientSideContentFilter";
import ContentFilterValues from "../model/ContentFilter";
import { useIntl } from "react-intl";
import get from "lodash/get";
import { useMfwThemeProvider } from 'myfitworld-utils';


type OrderBy = {
  key: string,
  order: "asc" | "desc"
}

const ExerciseProviderContext = createContext<{
  exercises: Exercise[] | null,
  isLoading: boolean,
  setContentFilter?: (contentFilter: ContentFilterValues) => void,
  setOrderBy?: (orderBy: OrderBy) => void
}>({exercises: null, isLoading: false})

export default ({children}: PropsWithChildren<any>) => {
  const { locale } = useIntl()
  const [exercises, setExercises] = useState<Exercise[] | null>(null);

  const [contentFilter, setContentFilter] = useState<ContentFilterValues | undefined>();
  const [orderBy, setOrderBy] = useState<OrderBy>({key: `title.${locale}`, order: "asc"});
  const [isLoading, setIsLoading] = useState(false)

  const {organizationId} = useOrganization();
  const {theme} = useMfwThemeProvider();

  useEffect(() => {
    if (!organizationId) return;
  
    setIsLoading(true);
  
    const exercisesRef = filterByOrganization(
      "exercises",
      organizationId,
      firestore.collection("exercises")
    );
  
    const unsubscribe = exercisesRef.onSnapshot(
      (snapshot) => {
        const exercises = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() } as Exercise)
        );
  
        const filtered = contentFilter
          ? clientSideContentFilter(exercises, contentFilter, theme.orgId)
          : exercises;
  
        const sorted = orderBy
          ? [...filtered].sort((a, b) =>
              get(a, orderBy.key)?.localeCompare(get(b, orderBy.key))
            )
          : filtered;
  
        setExercises(sorted);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching exercises:", error);
        setExercises(null);
        setIsLoading(false);
      }
    );
  
    return () => unsubscribe();
  
  }, [organizationId, contentFilter, orderBy]);
  

  return <ExerciseProviderContext.Provider value={{exercises: exercises, isLoading, setContentFilter, setOrderBy}}>
    {children}
  </ExerciseProviderContext.Provider>
}


export const useExerciseProvider = () => {
  const context = useContext(ExerciseProviderContext);

  if (context === undefined) {
    throw new Error(
      'useExerciseProvider must be used within an ExerciseProvider'
    );
  }
  return context;
};
